import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as groupsService from "../services/groupsService";

export function useGroupsQuery() {
  return useQuery({
    queryKey: ["groups"],
    queryFn: groupsService.listGroups,
  });
}

export function useGroupQuery(groupId) {
  return useQuery({
    queryKey: ["groups", groupId],
    queryFn: () => groupsService.getGroup({ groupId }),
    enabled: !!groupId,
  });
}

export function useMembershipsQuery() {
  return useQuery({
    queryKey: ["groupMemberships"],
    queryFn: groupsService.listAllMemberships,
  });
}

export function useUserMembershipsQuery(userId) {
  return useQuery({
    queryKey: ["userMemberships", userId],
    queryFn: () => groupsService.listMembershipsForUser({ userId }),
    enabled: !!userId,
  });
}

export function useCreateGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: groupsService.createGroup,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["groups"] }),
  });
}

export function useRenameGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: groupsService.renameGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}

export function useDeleteGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: groupsService.deleteGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}

export function useAddMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: groupsService.addMember,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      queryClient.invalidateQueries({ queryKey: ["groupMemberships"] });
      queryClient.invalidateQueries({ queryKey: ["userMemberships", variables.userId] });
    },
  });
}

// The code-based instant-join path — see groupsService.joinGroupByJoinCode.
// No userId in variables (the RPC derives the caller server-side), so the
// userMemberships invalidation is unscoped rather than keyed to one user.
export function useJoinGroupByJoinCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: groupsService.joinGroupByJoinCode,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      queryClient.invalidateQueries({ queryKey: ["groupMemberships"] });
      queryClient.invalidateQueries({ queryKey: ["userMemberships"] });
    },
  });
}

export function useRemoveMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: groupsService.removeMember,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      queryClient.invalidateQueries({ queryKey: ["groupMemberships"] });
      queryClient.invalidateQueries({ queryKey: ["userMemberships", variables.userId] });
    },
  });
}

export function useSetLeader() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: groupsService.setLeader,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}

export function useUpdateLeaderMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: groupsService.updateLeaderMessage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}

export function useRegenerateJoinCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: groupsService.regenerateJoinCode,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}

export function useSetWishlistDueDate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: groupsService.setWishlistDueDate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}

export function useClearWishlistDueDate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: groupsService.clearWishlistDueDate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}

export function useSetGiftExchangeDate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: groupsService.setGiftExchangeDate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}

export function useClearGiftExchangeDate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: groupsService.clearGiftExchangeDate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}

export function useSetWishlistUnlockOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: groupsService.setWishlistUnlockOverride,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}
